(() => {
  const config = window.spinToWinMagicLinksConfig;
  if (!config || !config.enabled) return;

  const DAY_IN_MS = 24 * 60 * 60 * 1000;
  const MAX_MAGIC_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
  const STORAGE_KEY = 'tss_spin_to_win_magic_link';
  const COOKIE_NAME = 'tss_spin_to_win_magic_link';
  const FALLBACK_MINIMUM_SPEND = 3000; // pence

  const normalizeCode = (code) => (typeof code === 'string' ? code.trim().toLowerCase() : '');
  const normalizeVariantId = (variantId) => {
    const parsed = Number(String(variantId || '').trim());
    return Number.isFinite(parsed) ? parsed : null;
  };

  const normalizedLinks = (Array.isArray(config.links) ? config.links : [])
    .map((link) => {
      const code = normalizeCode(link?.code);
      const variantId = normalizeVariantId(link?.variantId);
      if (!code || variantId === null) return null;
      return { code, variantId };
    })
    .filter(Boolean);

  if (!normalizedLinks.length) return;

  const linkLookup = new Map();
  const prizeVariantIds = new Set();

  normalizedLinks.forEach((link) => {
    linkLookup.set(link.code, link);
    prizeVariantIds.add(link.variantId);
  });

  const minimumSpend = Number.isFinite(Number(config.minimumSpend))
    ? Number(config.minimumSpend)
    : FALLBACK_MINIMUM_SPEND;
  const queryParam = (config.queryParam || 'magic_link').trim() || 'magic_link';

  const getCookie = (name) => {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    for (let i = 0; i < cookies.length; i += 1) {
      const parts = cookies[i].split('=');
      const cookieName = parts.shift();
      const cookieValue = parts.join('=');
      if (cookieName === name) {
        try {
          return decodeURIComponent(cookieValue);
        } catch (error) {
          return cookieValue;
        }
      }
    }
    return '';
  };

  const setCookie = (name, value, days) => {
    const expires = new Date(Date.now() + days * DAY_IN_MS).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  };

  const setStoredCode = (code) => {
    try {
      if (code) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, createdAt: Date.now() }));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      /* no-op */
    }

    try {
      if (code) {
        setCookie(COOKIE_NAME, `${code}|${Date.now()}`, 7);
      } else {
        setCookie(COOKIE_NAME, '', -1);
      }
    } catch (error) {
      /* no-op */
    }
  };

  const parseStoredCookie = (value = '') => {
    if (!value) return { code: '', createdAt: null };
    const parts = value.split('|');
    const code = parts[0] || '';
    const createdAt = Number(parts[1]);
    return { code, createdAt: Number.isFinite(createdAt) ? createdAt : null };
  };

  const getStoredCodeData = () => {
    let code = '';
    let createdAt = null;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        code = parsed?.code || '';
        createdAt = Number(parsed?.createdAt);
      }
    } catch (error) {
      code = '';
    }
    if (!code) {
      const cookieData = parseStoredCookie(getCookie(COOKIE_NAME));
      code = cookieData.code;
      createdAt = cookieData.createdAt;
    }
    return { code, createdAt: Number.isFinite(createdAt) ? createdAt : null };
  };

  const isExpired = (timestamp) => Number.isFinite(timestamp) && Date.now() - timestamp > MAX_MAGIC_AGE_MS;

  const getStoredCode = () => {
    const data = getStoredCodeData();
    if (isExpired(data.createdAt)) {
      setStoredCode('');
      return '';
    }
    return data.code;
  };

  const captureCodeFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const possibleKeys = [queryParam];

    if (!possibleKeys.includes('magic_link')) possibleKeys.push('magic_link');
    if (!possibleKeys.includes('klaviyo_magic_link')) possibleKeys.push('klaviyo_magic_link');

    for (const key of possibleKeys) {
      const value = params.get(key);
      const normalizedValue = normalizeCode(value);
      if (normalizedValue && linkLookup.has(normalizedValue)) {
        setStoredCode(value);
        return true;
      }
    }

    return false;
  };

  const getActiveLink = () => {
    const data = getStoredCodeData();
    if (isExpired(data.createdAt)) {
      setStoredCode('');
      return null;
    }

    const storedCode = normalizeCode(data.code);
    if (storedCode && linkLookup.has(storedCode)) {
      return linkLookup.get(storedCode);
    }
    return null;
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    return await response.json();
  };

  const calculateQualifiedSpend = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items.reduce((total, item) => {
      const variantId = Number(item.variant_id || item.id);
      const productType = (item.product_type || '').toLowerCase().trim();
      const isSewingMachine = productType === 'sewing machines' || productType === 'sewing machine';
      if (prizeVariantIds.has(variantId) || isSewingMachine) {
        return total;
      }
      const linePrice = Number(item.final_line_price ?? item.line_price ?? 0);
      return total + (Number.isFinite(linePrice) ? linePrice : 0);
    }, 0);
  };

  const getPrizeLines = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items.filter((item) => prizeVariantIds.has(Number(item.variant_id || item.id)));
  };

  let isAddingPrize = false;
  const addPrizeToCart = async (variantId) => {
    if (isAddingPrize) return null;
    isAddingPrize = true;
    try {
      const endpoint = window.routes?.cart_add_url || `${window.location.origin}/cart/add.js`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _magic_link_prize: 'true',
              },
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to add prize item');
      }

      return response.json().catch(() => null);
    } catch (error) {
      console.error('Error adding prize item:', error);
      return null;
    } finally {
      isAddingPrize = false;
    }
  };

  const changeLineItem = async (lineKey, quantity) => {
    const endpoint = window.routes?.cart_change_url || '/cart/change';
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity }),
    });

    if (!response.ok) {
      throw new Error('Unable to update prize line');
    }

    return response.json().catch(() => null);
  };

  const notifyCartUpdated = (openDrawer = false) => {
    if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    } else {
      document.dispatchEvent(new CustomEvent('cartChanged'));
    }

    if (openDrawer) {
      document.dispatchEvent(new Event('cartOpen'));
    }
  };

  let isSyncing = false;
  let shouldRerun = false;
  const scheduleSync = () => {
    
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(() => syncCartWithMagicLink());
    } else {
      window.setTimeout(() => syncCartWithMagicLink(), 0);
    }
  };

  const refreshCartDrawerSections = async () => {
    try {
      const response = await fetch('/?sections=cart-drawer');
      if (!response.ok) return;
      const sectionsJson = await response.json();
      const html = new DOMParser().parseFromString(sectionsJson['cart-drawer'], 'text/html');

      const newItems = html.querySelector('.cart-drawer__container');
      const newFooter = html.querySelector('main-cart-footer');
      const newOffer = html.querySelector('cart-drawer-offer-progress');
      const newBubble = html.querySelector('.cart-icon-bubble');

      const currentItems = document.querySelector('.cart-drawer__container');
      const currentFooter = document.querySelector('main-cart-footer');
      const currentOffer = document.querySelector('cart-drawer-offer-progress');
      const currentBubble = document.querySelector('.cart-icon-bubble');

      if (currentItems && newItems) {
        currentItems.innerHTML = newItems.innerHTML;
      }
      if (currentFooter && newFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
      }
      if (currentOffer && newOffer) {
        currentOffer.innerHTML = newOffer.innerHTML;
      }
      if (currentBubble && newBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
        currentBubble.style.display = 'flex';
      }
    } catch (error) {
      console.error('Unable to refresh cart drawer sections', error);
    }
  };

  const syncCartWithMagicLink = async () => {

    
    
    if (isSyncing) {
      shouldRerun = true;
      return;
    }

    isSyncing = true;
    try {

      
      const activeLink = getActiveLink();
      if (!activeLink && getStoredCode()) {
        setStoredCode('');
      }

      const cart = await fetchCart();
      const qualifiedSpend = calculateQualifiedSpend(cart);
      const prizeLines = getPrizeLines(cart);
      const activePrizeLine = activeLink
        ? prizeLines.find((item) => Number(item.variant_id || item.id) === activeLink.variantId)
        : null;
      const extraPrizeLines = prizeLines.filter(
        (item) => !activeLink || Number(item.variant_id || item.id) !== activeLink?.variantId
      );

      let cartMutated = false;
      let openDrawer = false;

      for (const line of extraPrizeLines) {
        await changeLineItem(line.key, 0);
        cartMutated = true;
      }

      if (activeLink) {
        if (qualifiedSpend >= minimumSpend) {
          
          if (!activePrizeLine) {
            await addPrizeToCart(activeLink.variantId);
            cartMutated = true;
            openDrawer = true;
          } else if (activePrizeLine.quantity !== 1) {
            await changeLineItem(activePrizeLine.key, 1);
            cartMutated = true;
          }
        } else if (prizeLines.length) {
          for (const line of prizeLines) {
            debugger
            await changeLineItem(line.key, 0);
            cartMutated = true;
          }
        }
      } else if (prizeLines.length) {
        for (const line of prizeLines) {
          await changeLineItem(line.key, 0);
          cartMutated = true;
        }
      }

      if (cartMutated) {
        await refreshCartDrawerSections();
        notifyCartUpdated(openDrawer);
      }
    } catch (error) {
      console.error('Spin to Win magic link sync error:', error);
    } finally {
      isSyncing = false;
      if (shouldRerun) {
        shouldRerun = false;
        syncCartWithMagicLink();
      }
    }
  };

  const registerCartListener = () => {
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleSync());
    } else {
      document.addEventListener('cartChanged', () => scheduleSync());
    }

    

    document.addEventListener('cartUpdate', () => scheduleSync());

    document.addEventListener('click', (event) => {
      if (event.target.closest('cart-remove-button') || event.target.closest('[data-cart-remove]')) {
        scheduleSync();
      }
    });
  };

  captureCodeFromUrl();
  registerCartListener();
  scheduleSync();
})();
